"use client";

import Link from "next/link";
import { useActionState, useRef, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { submitActivation } from "../actions/activation";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const initialState = {
	success: false,
	error: null,
	message: null,
};

export default function ActivarPage() {
	const [state, formAction, isPending] = useActionState(submitActivation, initialState);
	const [showPrivacyDetails, setShowPrivacyDetails] = useState(false);
	const formLoadedAt = useRef(Date.now().toString());

	return (
		<div className="font-sans min-h-screen bg-white text-text-primary">
			<Header />

			<main>
				{/* BLOQUE 1 · APERTURA */}
				<section className="px-6 md:px-12 py-24 md:py-32 max-w-4xl mx-auto text-center">
					<h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium text-text-primary leading-tight tracking-tight">
						Activar Kaizen Feedback en tu local
					</h1>
					<div className="mt-8 md:mt-10 text-xl text-text-secondary leading-relaxed max-w-2xl mx-auto space-y-4">
						<p>
							Has visto cómo funciona el sistema{" "}
							<br className="hidden md:inline" />
							y conoces las condiciones del plan estándar.
						</p>
						<p>
							Durante esta fase inicial,{" "}
							<br className="hidden md:inline" />
							activamos Kaizen Feedback de forma manual{" "}
							<br className="hidden md:inline" />
							para asegurarnos de que todo encaja bien desde el primer día.
						</p>
					</div>
				</section>

				{/* BLOQUE 2 · RECORDATORIO DE CONDICIONES */}
				<section className="px-6 md:px-12 py-16 md:py-20 bg-cream">
					<div className="max-w-2xl mx-auto">
						<div className="bg-white rounded-2xl p-8 md:p-10 shadow-sm">
							<div className="flex items-center gap-3 mb-6">
								<span className="w-3 h-3 rounded-full bg-olive"></span>
								<span className="text-olive font-medium text-lg">Plan Estándar — Precio de lanzamiento</span>
							</div>
							
							<div className="flex items-baseline gap-2 mb-6">
								<span className="font-display text-4xl md:text-5xl font-medium text-text-primary">299 €</span>
								<span className="text-xl text-text-secondary">/ año</span>
							</div>

							<ul className="space-y-3 text-text-secondary">
								<li className="flex items-start gap-3">
									<span className="text-olive mt-0.5">✓</span>
									<span>Pago anual único</span>
								</li>
								<li className="flex items-start gap-3">
									<span className="text-olive mt-0.5">✓</span>
									<span>1 local</span>
								</li>
								<li className="flex items-start gap-3">
									<span className="text-olive mt-0.5">✓</span>
									<span>Feedback privado ilimitado</span>
								</li>
								<li className="flex items-start gap-3">
									<span className="text-olive mt-0.5">✓</span>
									<span>Informes automáticos por email</span>
								</li>
								<li className="flex items-start gap-3">
									<span className="text-olive mt-0.5">✓</span>
									<span>Análisis con Mantener · Quitar · Añadir</span>
								</li>
								<li className="flex items-start gap-3">
									<span className="text-olive mt-0.5">✓</span>
									<span>1 expositor + pack inicial de 20 tarjetas</span>
								</li>
							</ul>

							<p className="mt-6 text-text-muted text-sm border-t border-border pt-6">
								Precio especial como agradecimiento por la confianza en esta fase inicial.
							</p>
						</div>
					</div>
				</section>

				{/* BLOQUE 3 · FORMULARIO */}
				<section className="px-6 md:px-12 py-24 md:py-32 bg-white">
					<div className="max-w-xl mx-auto">
						<div className="text-center mb-12">
							<h2 className="font-display text-2xl md:text-3xl font-medium text-text-primary">
								Formulario de activación
							</h2>
							<p className="mt-4 text-text-secondary text-lg leading-relaxed">
								Para activar el servicio necesitamos unos datos básicos{" "}
								<br className="hidden md:inline" />
								y poder coordinar contigo los siguientes pasos{" "}
								<br className="hidden md:inline" />
								(pago y envío del material).
							</p>
						</div>

						{state.success ? (
							<div className="bg-olive/10 border border-olive/30 rounded-2xl p-8 text-center">
								<div className="w-16 h-16 rounded-full bg-olive text-white flex items-center justify-center text-3xl mx-auto mb-6">
									✓
								</div>
								<h3 className="font-display text-2xl font-medium text-olive mb-4">
									¡Solicitud enviada!
								</h3>
								<p className="text-text-secondary text-lg leading-relaxed">
									{state.message}
								</p>
								<p className="mt-6 text-text-muted">
									Te contactaremos personalmente para confirmar la activación,{" "}
									<br className="hidden md:inline" />
									gestionar el pago y coordinar el envío del material.
								</p>
								<Link
									href="/"
									className="inline-block mt-8 text-olive hover:underline underline-offset-4"
								>
									Volver al inicio
								</Link>
							</div>
						) : (
							<form action={formAction} className="space-y-6">
								<input type="hidden" name="_loaded" value={formLoadedAt.current} />
								{/* Honeypot */}
								<div style={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden="true">
									<label htmlFor="website">Website</label>
									<input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" />
								</div>

								{/* Error message */}
								{state.error && (
									<div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
										{state.error}
									</div>
								)}

								{/* Nombre del negocio */}
								<div>
									<label htmlFor="businessName" className="block text-text-primary font-medium mb-2">
										Nombre del negocio <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										id="businessName"
										name="businessName"
										required
										placeholder="Cafetería La Esquina"
										className="w-full px-4 py-3 rounded-xl border border-border bg-white text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-olive/30 focus:border-olive transition-colors"
									/>
								</div>

								{/* Tipo de local */}
								<div>
									<label htmlFor="businessType" className="block text-text-primary font-medium mb-2">
										Tipo de local <span className="text-red-500">*</span>
									</label>
									<select
										id="businessType"
										name="businessType"
										required
										className="w-full px-4 py-3 rounded-xl border border-border bg-white text-text-primary focus:outline-none focus:ring-2 focus:ring-olive/30 focus:border-olive transition-colors appearance-none cursor-pointer"
										style={{
											backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23888888'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
											backgroundRepeat: "no-repeat",
											backgroundPosition: "right 1rem center",
											backgroundSize: "1.5em 1.5em",
											paddingRight: "3rem",
										}}
									>
										<option value="">Selecciona una opción</option>
										<option value="Cafetería">Cafetería</option>
										<option value="Restaurante">Restaurante</option>
										<option value="Otro">Otro</option>
									</select>
								</div>

								{/* Persona de contacto */}
								<div>
									<label htmlFor="contactName" className="block text-text-primary font-medium mb-2">
										Persona de contacto <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										id="contactName"
										name="contactName"
										required
										placeholder="Tu nombre"
										className="w-full px-4 py-3 rounded-xl border border-border bg-white text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-olive/30 focus:border-olive transition-colors"
									/>
								</div>

								{/* Email */}
								<div>
									<label htmlFor="email" className="block text-text-primary font-medium mb-2">
										Email <span className="text-red-500">*</span>
									</label>
									<input
										type="email"
										id="email"
										name="email"
										required
										placeholder="tu@email.com"
										className="w-full px-4 py-3 rounded-xl border border-border bg-white text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-olive/30 focus:border-olive transition-colors"
									/>
								</div>

								{/* Teléfono (opcional) */}
								<div>
									<label htmlFor="phone" className="block text-text-primary font-medium mb-2">
										Teléfono <span className="text-text-muted font-normal">(opcional)</span>
									</label>
									<input
										type="tel"
										id="phone"
										name="phone"
										placeholder="+34 600 000 000"
										className="w-full px-4 py-3 rounded-xl border border-border bg-white text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-olive/30 focus:border-olive transition-colors"
									/>
								</div>

								{/* Ciudad y país */}
								<div>
									<label htmlFor="location" className="block text-text-primary font-medium mb-2">
										Ciudad y país <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										id="location"
										name="location"
										required
										placeholder="Madrid, España"
										className="w-full px-4 py-3 rounded-xl border border-border bg-white text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-olive/30 focus:border-olive transition-colors"
									/>
									<p className="mt-1.5 text-text-muted text-sm">
										Para el envío del expositor y las tarjetas
									</p>
								</div>

								{/* Comentario opcional */}
								<div>
									<label htmlFor="comments" className="block text-text-primary font-medium mb-2">
										Comentario <span className="text-text-muted font-normal">(opcional)</span>
									</label>
									<textarea
										id="comments"
										name="comments"
										rows={4}
										placeholder="¿Hay algo que nos quieras hacer saber?"
										className="w-full px-4 py-3 rounded-xl border border-border bg-white text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-olive/30 focus:border-olive transition-colors resize-none"
									/>
								</div>

								{/* Política de privacidad */}
								<div className="bg-cream rounded-xl p-5">
									<div className="flex items-start gap-3">
										<input
											type="checkbox"
											id="acceptPrivacy"
											name="acceptPrivacy"
											value="yes"
											required
											className="mt-1 w-5 h-5 rounded border-border text-olive focus:ring-olive/30 cursor-pointer accent-olive"
										/>
										<label htmlFor="acceptPrivacy" className="text-text-secondary text-sm leading-relaxed cursor-pointer">
											He leído y acepto la{" "}
											<Link href="/privacidad" className="text-olive underline underline-offset-2">
												Política de Privacidad
											</Link>{" "}
											<span className="text-red-500">*</span>
										</label>
									</div>
									
									<button
										type="button"
										onClick={() => setShowPrivacyDetails(!showPrivacyDetails)}
										className="mt-3 text-text-muted text-xs flex items-center gap-1 hover:text-text-secondary transition-colors"
									>
										<span>{showPrivacyDetails ? "▼" : "▶"}</span>
										<span>Ver información sobre protección de datos</span>
									</button>
									
									{showPrivacyDetails && (
										<div className="mt-3 p-4 bg-white rounded-lg text-xs text-text-muted leading-relaxed space-y-2 border border-border">
											<p><strong>Responsable:</strong> Kaizen Feedback S.L.</p>
											<p><strong>CIF:</strong> ESB26818773</p>
											<p><strong>Dirección:</strong> Calle Gustavo Pérez Puig, 61. 28055. Madrid. España</p>
											<p><strong>Finalidad:</strong> Gestionar tu solicitud de activación del servicio Kaizen Feedback y contactarte para coordinar el pago y envío del material.</p>
											<p><strong>Legitimación:</strong> Tu consentimiento al enviar este formulario.</p>
											<p><strong>Destinatarios:</strong> Los datos no se cederán a terceros salvo obligación legal.</p>
											<p><strong>Derechos:</strong> Acceso, rectificación, supresión, limitación, oposición y portabilidad. Puedes ejercerlos escribiendo a contacto@kaizenfeedback.com</p>
											<p><strong>Información adicional:</strong> Consulta la <Link href="/privacidad" className="text-olive underline">Política de Privacidad</Link> completa.</p>
										</div>
									)}
								</div>

								{/* Turnstile */}
								{process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && (
									<Turnstile
										siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
										options={{ size: "flexible" }}
									/>
								)}

								{/* Submit button */}
								<div className="pt-4">
									<button
										type="submit"
										disabled={isPending}
										className="w-full rounded-full bg-olive text-white px-8 py-4 font-medium text-lg hover:bg-olive-light transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
									>
										{isPending ? (
											<span className="flex items-center justify-center gap-2">
												<span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
												Enviando...
											</span>
										) : (
											"Solicitar activación"
										)}
									</button>
									
									<p className="mt-4 text-text-muted text-sm text-center leading-relaxed">
										Tras enviar el formulario, te contactaremos personalmente{" "}
										<br className="hidden md:inline" />
										para confirmar la activación, gestionar el pago{" "}
										<br className="hidden md:inline" />
										y coordinar el envío del expositor y las tarjetas.
									</p>
								</div>
							</form>
						)}
					</div>
				</section>

				{/* BLOQUE 4 · TRANSPARENCIA */}
				<section className="px-6 md:px-12 py-24 md:py-32 bg-cream">
					<div className="max-w-2xl mx-auto text-center">
						<div className="space-y-4 text-xl text-text-secondary leading-relaxed">
							<p className="font-medium text-text-primary">
								No hay activaciones automáticas.
								<br />
								No hay cargos sin hablar antes.
							</p>
							<p>
								Este proceso nos permite{" "}
								<br className="hidden md:inline" />
								acompañarte mejor desde el principio{" "}
								<br className="hidden md:inline" />
								y asegurarnos de que Kaizen Feedback encaja en tu local.
							</p>
						</div>
					</div>
				</section>

				{/* BLOQUE 5 · ENLACE ALTERNATIVO */}
				<section className="px-6 md:px-12 py-16 md:py-20 bg-white">
					<div className="max-w-2xl mx-auto text-center">
						<p className="text-text-secondary text-lg">
							¿Tienes varios locales o un caso especial?
						</p>
						<p className="mt-2">
							<Link
								href="/contacto"
								className="text-olive hover:underline underline-offset-4 font-medium"
							>
								Hablemos primero →
							</Link>
						</p>
					</div>
				</section>
			</main>

			<Footer />
		</div>
	);
}
